/**
 * Decides what the password-reset page may show for a given return URL.
 *
 * Kept pure and separate from the route so the rule has ONE place and can be
 * tested: the page renders a password form only when this says `ready`.
 *
 * Background: Supabase evaluates the recovery link during client init. On
 * success it stores the session and emits `PASSWORD_RECOVERY`; on failure
 * (spent, expired or malformed link) it deliberately stores nothing and emits
 * nothing at all. Waiting for the event alone therefore leaves the page hanging
 * forever, which is what shipped a dead form to users.
 */
export type RecoveryLinkStatus = "ready" | "invalid";

/**
 * True when the URL carries a Supabase auth error. Both spellings occur: the
 * fragment usually holds `error`, `error_code` and `error_description`, but a
 * bare `error` without description is possible, and some flows use the query
 * string instead of the fragment.
 */
export function hasRecoveryError(hash: string, search: string): boolean {
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const query = new URLSearchParams(search.replace(/^\?/, ""));
  const param = (key: string) => fragment.get(key) ?? query.get(key);
  return param("error") !== null || param("error_description") !== null;
}

/**
 * `hasSession` must be read AFTER Supabase finished evaluating the URL —
 * `supabase.auth.getSession()` awaits that evaluation, so its result is the
 * decisive signal. An explicit error in the URL wins over an existing session:
 * the user followed a dead link and deserves to be told so.
 */
export function classifyRecoveryLink(input: {
  hash: string;
  search: string;
  hasSession: boolean;
}): RecoveryLinkStatus {
  if (hasRecoveryError(input.hash, input.search)) return "invalid";
  return input.hasSession ? "ready" : "invalid";
}

/**
 * True when this URL is Supabase's password-recovery return.
 *
 * Deliberately strict: only an explicit `type=recovery` counts. A bare
 * `access_token` or `code` also appears on a Google sign-in callback, and
 * treating those as a recovery would hijack normal logins.
 *
 * Used by the landing and login guards to stand down: both bounce a signed-in
 * visitor to /game, which would swallow the reset before the user ever sees the
 * password form. They must NOT redirect instead — the fragment has to survive
 * until Supabase has read it, and a redirect drops it.
 */
export function isRecoveryCallback(hash: string, search: string): boolean {
  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const query = new URLSearchParams(search.replace(/^\?/, ""));
  return (fragment.get("type") ?? query.get("type")) === "recovery";
}
